/**
 * Bounded command-based undo/redo (SPEC "Undo / Redo").
 *
 * Commands capture only the slice they changed (previous/next clip, previous/next livePose
 * entry, previous/next placement joint) — never a snapshot of the whole state. A continuous
 * drag is wrapped in `beginCoalesce(label, key)` / `endCoalesce()`, which merges every command
 * carrying that key into ONE entry: the first command's `undo`, the last command's `redo`.
 */

export interface UndoCommand {
  label: string;
  /**
   * Coalescing key. While a coalesce group with the same key is open, commands merge into a
   * single entry. A command with no key merges into whatever group is open; a command with a
   * DIFFERENT key closes the group and becomes its own entry, so entries never interleave.
   */
  key?: string;
  /**
   * Which slice of the state this command restores. Only meaningful inside a coalesce group: one
   * gesture may touch several slices (an IK drag writes the target AND two bone rotations per
   * solve), and the merged entry keeps the first `undo` / last `redo` **per slice** so undoing it
   * restores all of them. Commands without a slice merge as one, first-undo/last-redo.
   */
  slice?: string;
  undo(): void;
  redo(): void;
}

export interface UndoStack {
  readonly limit: number;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly undoLabel: string | undefined;
  readonly redoLabel: string | undefined;
  /** Number of undoable entries currently on the stack (test/debug aid). */
  readonly depth: number;
  /** Number of redoable entries currently on the stack (test/debug aid). */
  readonly redoDepth: number;
  readonly coalescing: boolean;
  push(command: UndoCommand): void;
  undo(): boolean;
  redo(): boolean;
  beginCoalesce(label: string, key: string): void;
  endCoalesce(): void;
  clear(): void;
}

export const UNDO_LIMIT = 200;

interface UndoEntry {
  label: string;
  key?: string;
  undo(): void;
  redo(): void;
}

interface CoalesceGroup {
  label: string;
  key: string;
  entry: UndoEntry | null;
  /**
   * The commands merged so far, one per `UndoCommand.slice`. An IK drag writes three slices per
   * solve (the target, the chain root's rotation, the mid's), so keeping only the group's very
   * first `undo` would restore the target and leave the pose behind. First `undo` wins per slice
   * (the state before the gesture), last `redo` wins (the final state).
   */
  parts: Map<string, { undo(): void; redo(): void }>;
}

export function createUndoStack(limit: number = UNDO_LIMIT): UndoStack {
  const undoEntries: UndoEntry[] = [];
  let redoEntries: UndoEntry[] = [];
  let group: CoalesceGroup | null = null;

  function pushEntry(entry: UndoEntry): void {
    undoEntries.push(entry);
    if (undoEntries.length > limit) undoEntries.splice(0, undoEntries.length - limit);
    redoEntries = [];
  }

  const stack: UndoStack = {
    get limit() {
      return limit;
    },
    get canUndo() {
      return undoEntries.length > 0;
    },
    get canRedo() {
      return redoEntries.length > 0;
    },
    get undoLabel() {
      return undoEntries.length > 0 ? undoEntries[undoEntries.length - 1].label : undefined;
    },
    get redoLabel() {
      return redoEntries.length > 0 ? redoEntries[redoEntries.length - 1].label : undefined;
    },
    get depth() {
      return undoEntries.length;
    },
    get redoDepth() {
      return redoEntries.length;
    },
    get coalescing() {
      return group !== null;
    },

    push(command: UndoCommand): void {
      if (group && (command.key === undefined || command.key === group.key)) {
        const open = group;
        const slice = command.slice ?? '';
        const existing = open.parts.get(slice);
        if (existing) existing.redo = command.redo;
        else open.parts.set(slice, { undo: command.undo, redo: command.redo });

        if (open.entry) return; // already on the stack; the parts map is what changed
        const entry: UndoEntry = {
          label: open.label,
          key: open.key,
          undo: (): void => {
            const parts = [...open.parts.values()].reverse();
            for (const part of parts) part.undo();
          },
          redo: (): void => {
            for (const part of open.parts.values()) part.redo();
          },
        };
        open.entry = entry;
        pushEntry(entry);
        return;
      }
      // A command from a different interaction ends the open group, so entries never interleave.
      if (group) group = null;
      pushEntry({
        label: command.label,
        key: command.key,
        undo: command.undo,
        redo: command.redo,
      });
    },

    undo(): boolean {
      if (group) stack.endCoalesce();
      const entry = undoEntries.pop();
      if (!entry) return false;
      entry.undo();
      redoEntries.push(entry);
      return true;
    },

    redo(): boolean {
      if (group) stack.endCoalesce();
      const entry = redoEntries.pop();
      if (!entry) return false;
      entry.redo();
      undoEntries.push(entry);
      return true;
    },

    beginCoalesce(label: string, key: string): void {
      group = { label, key, entry: null, parts: new Map() };
    },

    endCoalesce(): void {
      group = null;
    },

    clear(): void {
      undoEntries.length = 0;
      redoEntries = [];
      group = null;
    },
  };

  return stack;
}
